import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import '../../styles/Academy.css';
import Navbar from '../../components/Navbar';

export const courses = [
  {
    id: 'ethereum-101',
    title: "Ethereum Trading 101: A Beginner-Friendly Course",
    description: "Master the fundamentals of Ethereum trading, understand the technology behind ETH, and learn essential trading concepts for the crypto ecosystem.",
    isGuestAccessible: true,
    image: "https://cryptodnes.bg/en/wp-content/uploads/sites/2/2022/07/ethereum-2.0.jpg",
    duration: "2 hours",
    level: "Beginner",
    totalLessons: 16,
    modules: [
      {
        title: "Introduction to Ethereum Trading",
        lessons: [
          "What is Ethereum?",
          "Why ETH Matters",
          "Basic Trading Concepts",
          "Understanding Gas Fees"
        ]
      },
      {
        title: "Swapping and Bridging",
        lessons: [
          "DEX Trading Basics",
          "Using Liquidity Pools",
          "Cross-chain Bridges",
          "Managing Transaction Costs"
        ]
      },
      {
        title: "Essential Tools and Security",
        lessons: [
          "Wallets and Storage",
          "Block Explorers",
          "Price Tracking Tools",
          "Security Best Practices"
        ]
      },
      {
        title: "Risk Management",
        lessons: [
          "Common Crypto Scams",
          "Safe Trading Practices",
          "Portfolio Management",
          "Market Analysis Basics"
        ]
      }
    ]
  }
];

export default function Academy() {
  const { currentUser, isGuestUser, setGuestUser } = useAuth();
  const navigate = useNavigate();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');

  const handleCourseClick = (course) => {
    console.log("Button clicked!", course);
    
    if (!currentUser && !isGuestUser() && !course.isGuestAccessible) {
      setSelectedCourse(course);
      setShowAuthModal(true);
    } else {
      if (course.isGuestAccessible && !currentUser && !isGuestUser()) {
        setGuestUser();
      }
      navigate(`/academy/course/${course.id}`);
    }
  };

  const filteredCourses = activeFilter === 'all' 
    ? courses 
    : courses.filter(course => course.level.toLowerCase() === activeFilter);

  return (
    <div className="academy-page">
      <Navbar />
      {/* Hero Section */}
      <div className="academy-hero">
        <div className="hero-content">
          <div className="badge-container">
            <span className="academy-badge">FirmAI Academy</span>
          </div>
          <h1>Learn to Trade with <span className="gradient-text">Confidence</span></h1>
          <p>Start your journey with our free Ethereum course and advance to AI-powered trading strategies</p>
        </div>
      </div>

      {/* Course Filters */}
      <div className="course-filters">
        <div className="filter-group">
          <button 
            className={`filter-button ${activeFilter === 'all' ? 'active' : ''}`}
            onClick={() => setActiveFilter('all')}
          >
            All Courses
          </button>
          <button 
            className={`filter-button ${activeFilter === 'beginner' ? 'active' : ''}`}
            onClick={() => setActiveFilter('beginner')}
          >
            Beginner
          </button>
        </div>
      </div>

      {/* Courses Grid */}
      <div className="courses-container">
        <div className="courses-grid">
          {filteredCourses.map((course) => (
            <div key={course.id} className="course-card">
              <div className="course-banner">
                <img src={course.image} alt={course.title} />
                <div className="course-level">
                  <span>{course.level}</span>
                </div>
                {course.isGuestAccessible && (
                  <div className="course-badge">
                    <span>Free Access</span>
                  </div>
                )}
              </div>
              <div className="course-content">
                <div>
                  <h3>{course.title}</h3>
                  <p>{course.description}</p>
                  
                  <div className="course-details">
                    <div className="detail">
                      <span className="icon">⏱️</span>
                      <span>{course.duration}</span>
                    </div>
                    <div className="detail">
                      <span className="icon">📚</span>
                      <span>{course.modules.length} Modules</span>
                    </div>
                    <div className="detail">
                      <span className="icon">📝</span>
                      <span>{course.totalLessons} Lessons</span>
                    </div>
                  </div>

                  <div className="course-modules">
                    <h4>What you'll learn:</h4>
                    <ul>
                      {course.modules.map((module, index) => (
                        <li key={index}>
                          <span className="module-title">{module.title}</span>
                          <span className="lesson-count">{module.lessons.length} lessons</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                
                <button
                  className="course-button"
                  onClick={() => handleCourseClick(course)}
                >
                  {course.isGuestAccessible || currentUser || isGuestUser() ? 'Start Learning' : 'Sign in to Access'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Auth Modal */}
      {showAuthModal && (
        <div className="modal-overlay">
          <div className="auth-modal">
            <div className="modal-header">
              <h3>Sign in Required</h3>
              <button
                onClick={() => setShowAuthModal(false)}
                className="close-button"
              >
                ×
              </button>
            </div>
            
            <div className="modal-content">
              <p>This course requires a free account to access premium content.</p>
              <div className="modal-buttons">
                <button
                  onClick={() => {
                    setShowAuthModal(false);
                    navigate('/academy/auth', { 
                      state: { returnTo: `/academy/course/${selectedCourse?.id}` }
                    });
                  }}
                  className="primary-button"
                >
                  Sign In
                </button>
                <button
                  onClick={() => setShowAuthModal(false)}
                  className="secondary-button"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
